'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'

import type { JobDescription, Location } from '@types'

import JobTypeAhead from '@components/inputs/typeahead/job-typeahead'
import LocationTypeAhead from '@components/inputs/typeahead/location-typeahead'
import useLocale from '@hooks/client/useLocale'
import useHttpRequest from '@hooks/useHttpRequest'

import styles from '../jobs.module.scss'

const defaultLocation: Location = {
  name: '',
  location: [],
  displayName: '',
  type: '',
  county: '',
  state: '',
  postcode: '',
  metropolis: '',
  active: true,
  id: '',
}

export default function JobsLandingPage(): JSX.Element {
  const router = useRouter()
  const locale = useLocale()
  const { get } = useHttpRequest()
  const [job, setJob] = useState<JobDescription>({ micro: '', macro: '' })
  const [location, setLocation] = useState<Location>(defaultLocation)
  const [showError, setShowError] = useState(false)
  const [showJobError, setShowJobError] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const handleSearchSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    
    if (isSearching) return // Prevent double submissions
    
    setIsSearching(true)

    // Try to get typed values from DOM inputs - typeahead wraps input in a div
    const locationTypeahead = document.querySelector('[data-testid="location-typeahead"]')
    const locationInput = locationTypeahead?.querySelector('input') as HTMLInputElement
    const jobTypeahead = document.querySelector('[data-testid="job-typeahead"]')
    const jobInput = jobTypeahead?.querySelector('input') as HTMLInputElement
    
    // Use typed values if available, otherwise use location/job objects
    const jobValue = (jobInput?.value?.trim() || job.micro?.trim() || '').trim()
    const typedLocationText = (locationInput?.value?.trim() || location.name?.trim() || location.displayName?.trim() || '').trim()
    
    // If user selected from dropdown (location.id exists), use that selection
    // Only call autocomplete API if user typed but didn't select (no location.id)
    let finalLocation = location
    if (location.id && location.name) {
      // User selected from dropdown - use that exact selection
      finalLocation = location
    } else if (typedLocationText && !location.id) {
      try {
        const autocompleteResults = await get<Location[]>(
          `locations/autocomplete?query=${encodeURIComponent(typedLocationText)}`,
          { service: 'web' },
        )
        if (autocompleteResults && autocompleteResults.length > 0) {
          // Use the first result from autocomplete (same as what /us/jobs would use)
          const firstResult = autocompleteResults[0]
          finalLocation = {
            name: firstResult.name,
            id: firstResult.id,
            type: firstResult.type,
            location: firstResult.location?.map((p: string | number) => String(p)),
            displayName: firstResult.displayName,
            metropolis: firstResult.metropolis,
            county: firstResult.county,
            state: firstResult.state?.toUpperCase() || '',
            postcode: firstResult.postcode,
            active: firstResult.active,
          }
        } else {
          // If no autocomplete results, use typed text as location_name only
          finalLocation = {
            ...defaultLocation,
            name: typedLocationText,
            displayName: typedLocationText,
          }
        }
      } catch (error) {
        console.error('Error fetching location autocomplete:', error)
        // If API call fails, use typed text as location_name only
        finalLocation = {
          ...defaultLocation,
          name: typedLocationText,
          displayName: typedLocationText,
        }
      }
    } else if (!typedLocationText && location.name) {
      finalLocation = location
    }
    
    const locationValue = finalLocation.displayName || finalLocation.name || typedLocationText
    const isLocationMissing = !locationValue && !finalLocation?.id?.trim()

    setShowJobError(false) // Job title is optional
    setShowError(isLocationMissing)

    if (isLocationMissing) {
      setErrorMessage('Please enter a location to search.')
      setIsSearching(false)
      return
    }

    setErrorMessage('')

    // Build URL params - match exactly what /us/jobs uses via getSerpQueryString
    const params = new URLSearchParams()
    // Only set query if job title is provided (it's optional)
    if (jobValue) {
      params.set('query', jobValue)
      if (job.macro?.trim()) {
        params.set('macro', job.macro.trim())
      }
    }
    // Always set location_name
    params.set('location_name', locationValue)
    // Set location_id if we have it (from selection or autocomplete)
    if (finalLocation.id?.trim()) {
      params.set('location_id', finalLocation.id.trim())
    }
    // Set location_state if available
    if (finalLocation.state?.trim()) {
      params.set('location_state', finalLocation.state.trim().toUpperCase())
    }
    if (finalLocation.county?.trim()) {
      params.set('location_county', finalLocation.county.trim())
    }

    // Set default filter values
    params.set('full_time', 'true')
    params.set('part_time', 'true')
    params.set('no_experience', 'false')
    params.set('sort', 'FEATURED')
    params.set('page', '1')
    params.set('remote', 'false')
    // Use 40.234 km (25 miles) as max radius, same as /us/jobs
    params.set('max_radius', '40.234')

    // Use replace to update URL and trigger re-render with new search params
    router.replace(`/${locale}/v1/jobs?${params.toString()}`, { scroll: false })
    setIsSearching(false)
  }

  return (
    <section className={styles.jobsLandingPageSection}>
      <div className={styles.jobsLandingPageContainer}>
        <h1 className={styles.jobsLandingPageTitle}>Browse jobs</h1>
        <p className={styles.jobsLandingPageDescription}>
          Search thousands of jobs and apply with one click using SonicJobs AI.
        </p>
        <form className={styles.jobsLandingPageForm} onSubmit={handleSearchSubmit}>
          <div className={styles.jobsLandingPageSearchBox}>
            <div className={styles.jobsLandingPageInputWrapper}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={styles.jobsLandingPageSearchIcon}
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.3-4.3"></path>
              </svg>
              <JobTypeAhead
                className={styles.jobsLandingPageJobTypeahead}
                currentJob={job}
                setJob={setJob}
                showError={showJobError}
                setShowError={setShowJobError}
                placeholder="Job title or keyword"
              />
            </div>
            <div className={styles.jobsLandingPageInputWrapper}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={styles.jobsLandingPageLocationIcon}
              >
                <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              <LocationTypeAhead
                className={styles.jobsLandingPageLocationTypeahead}
                currentLocation={location}
                setLocation={setLocation}
                showError={showError}
                setShowError={setShowError}
                placeholder="City, state, or zip"
              />
            </div>
            <button type="submit" className={styles.jobsLandingPageSearchButton}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={styles.jobsLandingPageSearchButtonIcon}
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.3-4.3"></path>
              </svg>
              <span>Search</span>
            </button>
          </div>
        </form>
        {errorMessage && <div className={styles.jobsLandingPageError}>{errorMessage}</div>}
      </div>
    </section>
  )
}
